import React, { useContext, useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import InfoChip from './InfoChip';

function TenantApplications() {
  const { user, authReady } = useContext(AuthContext);
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [activeTab, setActiveTab] = useState('summary');
  const [viewingBookings, setViewingBookings] = useState([]);
  const [cancelBookingId, setCancelBookingId] = useState(null);

  const confirmedBookingsCount = viewingBookings.filter(vb => vb.status === 'confirmed').length;
  const applicationsCount = applications.length;

  const fetchData = useCallback(async () => {
    if (!authReady) return;
    if (!user || user.userType !== 'tenant') {
      navigate('/login');
      return;
    }
    try {
      const [appsRes, vbRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_BASE_URL}/api/tenants/applications`, {
          headers: { 'Authorization': `Bearer ${user.token}` }
        }),
        fetch(`${process.env.REACT_APP_API_BASE_URL}/api/tenants/viewings`, {
          headers: { 'Authorization': `Bearer ${user.token}` }
        })
      ]);
      const data = await appsRes.json().catch(() => ({}));
      if (!appsRes.ok) throw new Error(data.error || 'Failed to load applications');
      setApplications(data.applications || []);

      if (vbRes.ok) {
        const vbData = await vbRes.json();
        setViewingBookings(vbData.viewings || []);
      }

      await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/applications/mark-viewed`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      window.dispatchEvent(new Event('applications:viewed'));
    } catch (err) {
      setError(err.message || 'Could not load applications.');
    } finally {
      setLoading(false);
    }
  }, [user, navigate, authReady]);

  useEffect(() => {
    if (!authReady) return;
    fetchData();
  }, [fetchData, authReady]);

  const handleCancelBooking = async (bookingId) => {
    if (!user?.token || cancelBookingId) return;
    if (!window.confirm('Cancel this viewing?')) return;
    setCancelBookingId(bookingId);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/viewings/${bookingId}/cancel`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${user.token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to cancel viewing');
      await fetchData();
      setError('');
      setSuccessMessage('Viewing cancelled.');
      window.setTimeout(() => setSuccessMessage(''), 2500);
    } catch (err) {
      setError(err.message || 'Failed to cancel viewing');
      setSuccessMessage('');
    } finally {
      setCancelBookingId(null);
    }
  };

  if (!authReady) return <main className="App-main">Loading applications...</main>;
  if (loading) return <main className="App-main">Loading applications...</main>;

  return (
    <main className="App-main tenants-page">
      <div className="ledger-header">
        <div>
          <p className="eyebrow-tag">Applications</p>
          <h2>Applications</h2>
          <p>Track your viewings and applications in one place.</p>
        </div>
      </div>
      <div className="subnav-wrap" style={{ marginBottom: '1rem' }}>
        <nav className="analytics-subnav" aria-label="Application sections">
          <button type="button" className={activeTab === 'summary' ? 'primary-button' : 'secondary-button'} onClick={() => setActiveTab('summary')}>Summary</button>
          <button type="button" className={activeTab === 'viewings' ? 'primary-button' : 'secondary-button'} onClick={() => setActiveTab('viewings')}>Viewings</button>
          <button type="button" className={activeTab === 'applications' ? 'primary-button' : 'secondary-button'} onClick={() => setActiveTab('applications')}>Applications</button>
        </nav>
      </div>

      {error && <p className="error-message">{error}</p>}
      {successMessage && <p className="success-message">{successMessage}</p>}

      {activeTab === 'summary' && (
        <div className="dashboard-section">
          <h3>Summary</h3>
          <div className="inquiry-list">
            <div className="inquiry-card">
              <p><strong>Scheduled viewings</strong></p>
              <p className="status-text">{confirmedBookingsCount} upcoming</p>
              <button className="secondary-button" onClick={() => setActiveTab('viewings')}>View viewings</button>
            </div>
            <div className="inquiry-card">
              <p><strong>Your applications</strong></p>
              <p className="status-text">{applicationsCount} total</p>
              <button className="secondary-button" onClick={() => setActiveTab('applications')}>View applications</button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'viewings' && (
        <div className="dashboard-section" id="scheduled-viewings">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            <h3 style={{ margin: 0 }}>Scheduled viewings</h3>
            <InfoChip label="Viewing help">
              Book viewings from any property listing page. Cancel upcoming viewings here.
            </InfoChip>
          </div>

          {viewingBookings.length > 0 ? (
            <div className="inquiry-list" style={{ marginTop: '0.75rem' }}>
              {viewingBookings
                .sort((a, b) => new Date(a.start_time) - new Date(b.start_time))
                .map((vb) => {
                  const startDate = new Date(vb.start_time);
                  const isPast = startDate < new Date();
                  const isCancelled = vb.status === 'cancelled';
                  return (
                    <div key={`vb-${vb.id}`} className="inquiry-card" style={isCancelled ? { opacity: 0.6 } : {}}>
                      <p><strong>Property:</strong> {vb.property_address}{vb.property_city ? `, ${vb.property_city}` : ''}</p>
                      <p><strong>Status:</strong> {isCancelled ? 'Cancelled' : isPast ? 'Completed' : 'Confirmed'}</p>
                      <p><strong>Viewing:</strong> {startDate.toLocaleString('en-CA', { timeZone: 'America/Toronto', dateStyle: 'full', timeStyle: 'short' })}</p>
                      {vb.status === 'confirmed' && !isPast && (
                        <div className="form-actions" style={{ marginTop: '0.5rem' }}>
                          <button
                            className="secondary-button"
                            onClick={() => handleCancelBooking(vb.id)}
                            disabled={cancelBookingId === vb.id}
                          >
                            {cancelBookingId === vb.id ? 'Cancelling...' : 'Cancel viewing'}
                          </button>
                        </div>
                      )}
                      <p className="timestamp">Booked on: {new Date(vb.booked_at).toLocaleDateString()}</p>
                    </div>
                  );
                })}
            </div>
          ) : (
            <div style={{ marginTop: '0.75rem' }}>
              <p>No scheduled viewings yet.</p>
              <div className="form-actions" style={{ marginTop: '0.5rem' }}>
                <Link className="secondary-button" to="/properties">Browse listings</Link>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'applications' && (
        <div className="dashboard-section">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            <h3 style={{ margin: 0 }}>Your Applications</h3>
            <InfoChip label="Application tips">
              Each card shows the latest status; tap "View / Manage" to upload docs or message the landlord.
            </InfoChip>
          </div>
          {applications.length > 0 ? (
            <div className="application-list">
              {applications.map((app) => {
                const steps = ['submitted', 'in_review', 'approved', 'completed'];
                const stepLabels = ['Submitted', 'In Review', 'Approved', 'Move-in'];
                const currentStatus = app.status === 'docs_requested' || app.status === 'info_needed' ? 'in_review'
                  : app.status === 'lease_drafted_landlord_signed' || app.status === 'lease_tenant_signed' ? 'approved'
                  : app.status;
                const isRejected = app.status === 'rejected';
                const currentIdx = isRejected ? -1 : steps.indexOf(currentStatus);

                return (
                <div key={app.id} className="application-card">
                  <p><strong>Property:</strong> {app.property_address}, {app.property_city}</p>
                  <p><strong>Landlord:</strong> {app.landlord_email}</p>

                  {/* Progress bar */}
                  {isRejected ? (
                    <div style={{ margin: '0.75rem 0', padding: '0.5rem 0.75rem', background: '#fef2f2', borderRadius: '6px', border: '1px solid #fecaca' }}>
                      <p style={{ margin: 0, color: '#dc2626', fontWeight: 600, fontSize: '0.85rem' }}>Application not approved</p>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0', margin: '0.75rem 0' }}>
                      {steps.map((step, idx) => {
                        const isActive = idx <= currentIdx;
                        const isCurrent = idx === currentIdx;
                        return (
                          <React.Fragment key={step}>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                              <div style={{
                                width: '24px', height: '24px', borderRadius: '50%',
                                background: isActive ? '#2563eb' : '#e5e7eb',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                border: isCurrent ? '2px solid #1d4ed8' : 'none',
                                transition: 'all 0.2s'
                              }}>
                                {isActive && <span style={{ color: '#fff', fontSize: '12px' }}>✓</span>}
                              </div>
                              <span style={{
                                fontSize: '0.7rem', marginTop: '4px', color: isActive ? '#1d4ed8' : '#9ca3af',
                                fontWeight: isCurrent ? 600 : 400, textAlign: 'center'
                              }}>{stepLabels[idx]}</span>
                            </div>
                            {idx < steps.length - 1 && (
                              <div style={{
                                flex: 1, height: '2px', marginTop: '-16px',
                                background: idx < currentIdx ? '#2563eb' : '#e5e7eb'
                              }} />
                            )}
                          </React.Fragment>
                        );
                      })}
                    </div>
                  )}

                  {app.status === 'docs_requested' && (
                    <div style={{ fontSize: '0.8rem', color: '#d97706', margin: '0.5rem 0', background: '#fffbeb', padding: '0.5rem 0.75rem', borderRadius: '6px', border: '1px solid #fde68a' }}>
                      <p style={{ margin: 0, fontWeight: 600 }}>⚠️ Documents requested</p>
                      {app.requested_doc_types && app.requested_doc_types.length > 0 && (
                        <ul style={{ margin: '0.25rem 0 0 1rem', padding: 0 }}>
                          {app.requested_doc_types.map((t, i) => (
                            <li key={i} style={{ margin: '0.15rem 0' }}>{t.replace(/_/g, ' ')}</li>
                          ))}
                        </ul>
                      )}
                      {app.doc_request_note && (
                        <p style={{ margin: '0.25rem 0 0', fontStyle: 'italic' }}>"{app.doc_request_note}"</p>
                      )}
                    </div>
                  )}
                  <p className="timestamp">Submitted on: {new Date(app.created_at).toLocaleDateString()}</p>
                  <Link to={`/application/${app.id}/documents`} className="secondary-button" style={{ marginTop: '0.5rem' }}>View / Manage</Link>
                </div>
                );
              })}
            </div>
          ) : (
            <div>
              <p>You have not submitted any applications yet.</p>
              <div className="form-actions" style={{ marginTop: '0.5rem' }}>
                <Link className="secondary-button" to="/properties">Browse listings</Link>
              </div>
            </div>
          )}
        </div>
      )}
    </main>
  );
}

export default TenantApplications;
